import { supabase } from '../../supabase/client';
import { ScheduleInsert, ScheduleUpdate, Schedule, ScheduleWithTeam, ScheduleStatus } from '../../../types/schedule';
import { PostgrestError } from '@supabase/supabase-js';

// Check if supabase client is available
if (!supabase) {
  throw new Error('Supabase client not initialized');
}

export const scheduleService = {
  /**
   * Create a new schedule
   */
  async createSchedule(schedule: ScheduleInsert): Promise<{ data: Schedule | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    console.log('Creating schedule with data:', schedule);
    
    try {
      const { data, error } = await supabase
        .from('schedules')
        .insert(schedule)
        .select('*')
        .single();
      
      if (error) {
        console.error('Supabase API error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
      }
      
      return { data, error };
    } catch (error) {
      console.error('Exception caught in createSchedule:', error);
      return { 
        data: null, 
        error: error as PostgrestError 
      };
    }
  },

  /**
   * Get a schedule by ID
   */
  async getScheduleById(id: string): Promise<{ data: ScheduleWithTeam | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('schedules')
      .select(`
        *,
        team:teams (
          id,
          name
        )
      `)
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  /**
   * Get all schedules for a team
   */
  async getSchedulesByTeamId(teamId: string): Promise<{ data: Schedule[] | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('schedules')
      .select('*')
      .eq('team_id', teamId)
      .order('start_date', { ascending: false });
    
    return { data, error };
  },

  /**
   * Update a schedule
   */
  async updateSchedule(id: string, updates: ScheduleUpdate): Promise<{ data: Schedule | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('schedules')
      .update(updates)
      .eq('id', id)
      .select('*')
      .single();
    
    return { data, error };
  },

  /**
   * Update schedule status
   */
  async updateScheduleStatus(id: string, status: ScheduleStatus): Promise<{ data: Schedule | null; error: PostgrestError | null }> {
    return this.updateSchedule(id, { status });
  },

  /**
   * Delete a schedule
   */
  async deleteSchedule(id: string): Promise<{ error: PostgrestError | null }> {
    if (!supabase) {
      return { error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { error } = await supabase
      .from('schedules')
      .delete()
      .eq('id', id);
    
    return { error };
  },

  /**
   * Open schedule for preferences
   */
  async openForPreferences(
    id: string, 
    dueDate: string
  ): Promise<{ data: Schedule | null; error: PostgrestError | null }> {
    return this.updateSchedule(id, { 
      status: 'preferences_open',
      preferences_due_date: dueDate
    });
  },

  /**
   * Close preferences for a schedule
   */
  async closePreferences(id: string): Promise<{ data: Schedule | null; error: PostgrestError | null }> {
    return this.updateSchedule(id, { status: 'preferences_closed' });
  },

  /**
   * Publish a schedule
   */
  async publishSchedule(id: string): Promise<{ data: Schedule | null; error: PostgrestError | null }> {
    return this.updateSchedule(id, { 
      status: 'published',
      published_at: new Date().toISOString()
    });
  }
}; 